// Builds the LLM prompt that suggests brand-new boolean search queries for the
// Search landing page, seeded from the topics the user has recently explored
// (their recent searches and recent Assistant chat titles).
//
// The suggestions are NEW boolean queries adjacent to the user's recent
// interests — not rephrasings of what they already searched. The response is
// parsed by parseBooleanVariants in store/ui/sagas.js, so the output format
// here MUST match buildBooleanSearchPrompt's JSON contract. The scite boolean
// syntax + field schema are shared with that builder so the two stay in sync.

import { SYNTAX_GUIDE, buildFieldSchema } from './boolean-search-prompt'

export const MAX_SEED_TOPICS = 8
const MAX_TOPIC_CHARS = 200

const truncate = (text, max = MAX_TOPIC_CHARS) => {
  if (!text || text.length <= max) return text || ''
  return text.slice(0, max)
}

const formatSeedTopics = seedTopics => {
  const cleaned = (seedTopics || [])
    .map(t => truncate((t || '').trim()))
    .filter(Boolean)
    .slice(0, MAX_SEED_TOPICS)
  if (cleaned.length === 0) return null
  return cleaned.map((t, i) => `${i + 1}. ${t}`).join('\n')
}

const OUTPUT_FORMAT = `
OUTPUT FORMAT — return ONLY a single JSON object, with no prose before or after, no markdown headings, and no commentary. You may optionally wrap the object in a single \`\`\`json ... \`\`\` fenced code block, but nothing else may appear outside the fence.

The "variants" field MUST be a JSON array — that is, its value MUST start with "[" and end with "]". Multiple variant objects are separated by commas INSIDE the array brackets. Do NOT emit a series of bare objects without enclosing array brackets.

The exact shape (note the [ and ] around the list of variant objects):

{
  "variants": [
    {
      "query": "<boolean query string for a NEW search adjacent to the topics>",
      "label": "<at most 24 characters, e.g. 'Broad', 'Clinical trials', 'Recent reviews'>",
      "explanation": "<one short sentence describing what this search explores>"
    }
  ]
}

JSON ESCAPING (CRITICAL):
- Boolean queries frequently contain double-quoted phrases such as "non-small cell lung cancer". Inside the JSON "query" string, every such double quote MUST be escaped as \\". Example: "query": "(\\"asthma\\" | \\"reactive airway*\\") + treat*"
- Do NOT use smart/curly quotes (“ ”) — only straight ASCII double quotes
- Backslashes inside the query must also be JSON-escaped as \\\\
- The JSON must be valid and parseable by JSON.parse
`.trim()

/**
 * @param {string[]} seedTopics - The user's recent research topics (recent
 *   search terms and Assistant chat titles), most-relevant first.
 * @param {number} [count=3] - How many suggestions to ask for.
 * @returns {string} prompt text, or '' when there are no usable seed topics.
 */
export const buildSuggestedSearchesPrompt = (seedTopics, count = 3) => {
  const topicsBlock = formatSeedTopics(seedTopics)
  if (!topicsBlock) return ''

  return `
You suggest new boolean search queries for scite.ai's search engine. Below are the topics a researcher has recently explored (from their recent searches and questions to a scientific literature assistant), most relevant first. Based on the interests they reveal, suggest ${count} NEW boolean searches the same researcher might want to run next. Your output is consumed by software, so it must follow the format exactly.

Each suggestion MUST satisfy all of these:
- be a genuinely new search, NOT a repeat or trivial rephrasing of any recent topic listed below
- be recognisably adjacent to the topics below, so it is relevant to their interests
- be distinct from the other suggestions (cover different angles, not minor variants)
- be a syntactically valid boolean query using only the supported operators below

${SYNTAX_GUIDE}

${buildFieldSchema()}

${OUTPUT_FORMAT}

THE RESEARCHER'S RECENT TOPICS:
"""
${topicsBlock}
"""
`.trim()
}

export default buildSuggestedSearchesPrompt
